import React, { useContext, useState, useEffect } from 'react';
import { Box, Stack, TextField, MenuItem, FormControlLabel, Switch, Button, Typography, IconButton, Divider } from '@mui/material';
import PropTypes from 'prop-types';
import { useCreateQuizQuestionMutation, useCreateQuestionOptionMutation, useGetRequirementsByPosteQuery } from '../../services/SkillsManagement';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import { useDispatch } from 'react-redux';
import * as actions from '../../Reducers/Actions';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

// Right-pane form to create a question for a quiz
const CreateQuestion = ({ quizId, posteId, onCreated, onCancel }) => {
  const [createQuestion, { isLoading }] = useCreateQuizQuestionMutation();
  const [createOption] = useCreateQuestionOptionMutation();
  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);
  const dispatch = useDispatch();

  const [form, setForm] = useState({
    questionType: 'single_choice',
    questionText: '',
    hint: '',
    points: 1,
    timeLimit: 0,
    order: 1,
    isRequired: true,
    explanation: '',
    skillId: '',
  });

  const onChange = (field) => (e) => {
    const value =
      field === 'isRequired'
        ? Boolean(e.target.checked)
        : ['points', 'timeLimit', 'order'].includes(field)
        ? Number(e.target.value)
        : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!quizId || !String(quizId).trim()) return;
    try {
      // Ensure parent full-screen dialog reopens if a remount happens during the request
      try {
        sessionStorage.setItem('questionsDlgAutoReopen', 'true');
        sessionStorage.setItem('questionsDlgCtx', JSON.stringify({ quizId: Number(quizId), posteId: Number(posteId) || null }));
        // eslint-disable-next-line no-console
        console.debug('[CreateQuestion] set auto-reopen flag before create');
      } catch (_) { /* ignore */ }
      if (!form.skillId && form.skillId !== 0) {
        setInfoAlert({ severity: 'error', message: 'Please select a related skill.' });
        setOpenSnackBar(true);
        return;
      }

      const body = {
        quizId: Number(quizId),
        skillId: Number(form.skillId),
        questionType: form.questionType,
        questionText: form.questionText.trim(),
        hint: form.hint?.trim() || undefined,
        points: Number(form.points || 1),
        timeLimit: form.timeLimit ? Number(form.timeLimit) : undefined,
        order: Number(form.order || 1),
        isRequired: Boolean(form.isRequired),
        explanation: form.explanation?.trim() || undefined,
      };

      const res = await createQuestion({ body }).unwrap();
      const created = res?.data || res;
      
      // If user added options locally, create them now for the created question,
      // collect created results, and merge into the question in the reducer
      let createdOptions = [];
      if (Array.isArray(options) && options.length > 0) {
        const questionId = created?.questionId || created?.id;
        if (questionId) {
          const tasks = options
            .filter((o) => (o.optionText || '').trim())
            .map((o, idx) =>
              createOption({ body: {
                questionId: Number(questionId),
                optionText: o.optionText.trim(),
                isCorrect: Boolean(o.isCorrect),
                order: Number.isFinite(o.order) ? Number(o.order) : idx,
              } }).unwrap()
            );
          try {
            const results = await Promise.allSettled(tasks);
            const ok = (r) => r.status === 'fulfilled';
            const normalized = (o, fallback) => ({
              optionId: o?.questionOptionId ?? o?.optionId ?? o?.id ?? fallback?.optionId,
              optionText: o?.optionText ?? fallback?.optionText ?? '',
              isCorrect: typeof o?.isCorrect === 'boolean' ? o.isCorrect : Boolean(fallback?.isCorrect),
              order: Number(o?.order ?? fallback?.order ?? 0),
            });
            const createdOk = results.filter(ok).map((r, i) => normalized(r.value?.data || r.value || {}, options[i]));
            createdOptions = createdOk.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          } catch (_) {
            // If anything fails, keep local options so UI still updates
            createdOptions = options.slice().filter((o) => (o.optionText || '').trim()).map((o) => ({
              optionId: o?.optionId,
              optionText: o?.optionText || '',
              isCorrect: Boolean(o?.isCorrect),
              order: Number.isFinite(o?.order) ? Number(o.order) : 0,
            })).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          }
        }
      }

      setInfoAlert({ severity: 'success', message: 'Question created successfully' });
      setOpenSnackBar(true);
      // Keep auto-reopen flag through options creation and UI updates
      try { sessionStorage.setItem('questionsDlgAutoReopen', 'true'); } catch (_) { /* ignore */ }
      // Update reducer list with the created question
      dispatch({ type: actions.ADD_QUIZ_QUESTION, payload: created });
      // If we have created options, push them into the question immediately so the UI shows them
      if (createdOptions.length > 0) {
        dispatch({ type: actions.UPDATE_QUIZ_QUESTION, payload: { ...created, options: createdOptions } });
      }
      onCreated?.({ ...created, options: createdOptions });
      setForm((p) => ({ ...p, questionText: '', hint: '', explanation: '' }));
      setOptions([{ optionText: '', isCorrect: false, order: 0 }]);
    } catch (error) {
      const message = error?.data?.message || error?.message || 'Failed to create question';
      setInfoAlert({ severity: 'error', message: String(message) });
      setOpenSnackBar(true);
    }
  };

  // Inline options builder state and handlers
  const [options, setOptions] = useState([{ optionText: '', isCorrect: false, order: 0 }]);
  const addOptionRow = () => setOptions((prev) => [...prev, { optionText: '', isCorrect: false, order: prev.length }]);
  const removeOptionRow = (idx) => setOptions((prev) => prev.filter((_, i) => i !== idx).map((o, i) => ({ ...o, order: i })));
  const updateOption = (idx, field, value) => setOptions((prev) => prev.map((o, i) => (i === idx ? { ...o, [field]: value } : o)));

  // Load required skills by poste
  const { data: reqData, isLoading: skillsLoading, error: skillsError, refetch: refetchReqs } = useGetRequirementsByPosteQuery(
    {
      posteId: Number(posteId),
      pagination: JSON.stringify({ currentPage: 1, rowsPerPage: 1000 }),
      sorting: '',
    },
    { skip: !posteId, refetchOnMountOrArgChange: true }
  );

  const skills = Array.isArray(reqData?.requirements || reqData?.data?.requirements)
    ? (reqData?.requirements || reqData?.data?.requirements)
        .map((r, idx) => ({
          id: Number(r?.skill_id ?? r?.skillId ?? idx),
          name: r?.skill?.name || r?.skill_name || `Skill ${Number(r?.skill_id ?? r?.skillId ?? idx)}`,
          description: r?.skill?.description || r?.description || undefined,
        }))
        .filter((s) => Number.isFinite(s.id))
    : [];

  // Debug logging to help diagnose empty skills list
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug('Requirements query -> loading:', skillsLoading, 'error:', skillsError, 'raw:', reqData, 'parsed skills:', skills);
  }

  useEffect(() => {
    if (posteId) refetchReqs?.();
  }, [posteId, refetchReqs]);

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'grid', gap: 1.5 }}>
      <Typography variant="h6">New Question</Typography>
      {/* Row: Question Type + Related Skill */}
      <Stack direction="row" spacing={1.5}>
        <TextField
          size="small"
          select
          label="Question Type"
          value={form.questionType}
          onChange={onChange('questionType')}
          sx={{ flex: 1 }}
        >
          <MenuItem value="single_choice">Single choice</MenuItem>
          <MenuItem value="multiple_choice">Multiple choice</MenuItem>
          <MenuItem value="true_false">True / False</MenuItem>
          <MenuItem value="short_answer">Short answer</MenuItem>
        </TextField>

        <TextField
          size="small"
          select
          label="Related Skill"
          value={form.skillId}
          onChange={(e) => setForm((p) => ({ ...p, skillId: Number(e.target.value) }))}
          disabled={skillsLoading || !posteId}
          required
          error={Boolean(skillsError)}
          helperText={
            !posteId
              ? 'Select a position for this quiz to load requirements.'
              : skillsError
                ? 'Failed to load skills'
                : (skillsLoading ? 'Loading skills...' : (skills.length === 0 ? 'No skills found' : ' '))
          }
          sx={{ flex: 1 }}
        >
          {skills.map((s) => (
            <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
          ))}
        </TextField>
      </Stack>

      {/* Question full width */}
      <TextField
        size="small"
        label="Question"
        value={form.questionText}
        onChange={onChange('questionText')}
        required
        multiline
        minRows={2}
      />

      {/* Row: Order (small) + Points (small) + Time (small) + Required */}
      <Stack direction="row" spacing={1.5} alignItems="center">
        <TextField
          size="small"
          label="Order"
          type="number"
          value={form.order}
          onChange={onChange('order')}
          inputProps={{ min: 1, style: { textAlign: 'center' } }}
          sx={{ width: 110 }}
        />
        <TextField
          size="small"
          label="Points"
          type="number"
          inputProps={{ min: 1, style: { textAlign: 'center' } }}
          value={form.points}
          onChange={onChange('points')}
          sx={{ width: 110 }}
        />
        <TextField
          size="small"
          label="Time Limit (sec)"
          type="number"
          inputProps={{ min: 0, style: { textAlign: 'center' } }}
          value={form.timeLimit}
          onChange={onChange('timeLimit')}
          sx={{ width: 130 }}
        />
        <Box sx={{ flex: 1 }} />
        <FormControlLabel control={<Switch size="small" checked={form.isRequired} onChange={onChange('isRequired')} />} label="Required" />
      </Stack>

      {/* Row: Hint + Explanation */}
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <TextField size="small" label="Hint (optional)" value={form.hint} onChange={onChange('hint')} multiline minRows={2} sx={{ flex: 1 }} />
        <TextField size="small" label="Explanation (optional)" value={form.explanation} onChange={onChange('explanation')} multiline minRows={2} sx={{ flex: 1 }} />
      </Stack>

      

      {/* Options builder */}
      <Divider />
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1">Answer Options</Typography>
        <Button startIcon={<AddIcon />} onClick={addOptionRow} variant="outlined" size="small">
          Add Option
        </Button>
      </Stack>

      <Stack spacing={1}>
        {options.map((opt, idx) => (
          <Stack key={idx} direction="row" alignItems="center" spacing={1}>
            <FormControlLabel
              control={<Switch size="small" checked={opt.isCorrect} onChange={(e) => updateOption(idx, 'isCorrect', Boolean(e.target.checked))} />}
              label={opt.isCorrect ? 'Correct' : 'Incorrect'}
            />
            <TextField
              size="small"
              placeholder={`Option ${idx + 1}...`}
              value={opt.optionText}
              onChange={(e) => updateOption(idx, 'optionText', e.target.value)}
              fullWidth
            />
            <TextField
              size="small"
              label="Order"
              type="number"
              value={opt.order}
              onChange={(e) => updateOption(idx, 'order', Number(e.target.value))}
              inputProps={{ min: 0, style: { textAlign: 'center' } }}
              sx={{ width: 80 }}
            />
            <IconButton size="small" color="error" onClick={() => removeOptionRow(idx)} aria-label="remove option">
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>

      {/* Explanation moved up next to Hint */}

      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button size="small" variant="text" onClick={() => onCancel?.()}>Cancel</Button>
        <Button size="small" type="submit" variant="contained" disabled={isLoading || !form.questionText.trim()}>
          {isLoading ? 'Saving...' : 'Add Question'}
        </Button>
      </Stack>
    </Box>
  );
};

CreateQuestion.propTypes = {
  quizId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  posteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCreated: PropTypes.func,
  onCancel: PropTypes.func,
};

export default CreateQuestion;
